import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, ScanLine, Loader2, FileText, Sparkles, ExternalLink, Trash2, ImageIcon, FileIcon, FilePlus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

// PDF.js — worker is vendored locally (see /public/pdf.worker.min.mjs) so PDF
// conversion works even if the customer's network blocks the unpkg CDN.
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import logger from "@/lib/logger";
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

export default function OCRPage() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [processing, setProcessing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/ocr/documents");
      setHistory(data);
    } catch (err) {
      logger.warn("OCR history fetch failed:", err?.response?.status);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/devices");
        setProjects(data);
      } catch (err) {
        logger.warn("OCR devices fetch failed:", err?.response?.status);
      }
    })();
    loadHistory();
  }, []);

  // Convert first page of a PDF to a JPEG data URL so the existing image-only OCR
  // endpoint can handle it. We only render one page — Claude's vision model
  // extracts everything from a full-page render at 1.5x scale.
  const pdfToImage = async (file) => {
    setConverting(true);
    try {
      const buf = await file.arrayBuffer();
      const doc = await pdfjsLib.getDocument({ data: buf }).promise;
      if (doc.numPages > 1) {
        toast.message(`PDF has ${doc.numPages} pages — scanning page 1 only.`);
      }
      const page = await doc.getPage(1);
      const viewport = page.getViewport({ scale: 1.75 });
      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
      // JPEG @ 0.85 keeps the base64 within the 8 MB / 400 KB post-scan store budget.
      return canvas.toDataURL("image/jpeg", 0.85);
    } finally {
      setConverting(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File too large. Max 20 MB.");
      return;
    }
    setFileName(file.name);
    setResult(null);
    const type = (file.type || "").toLowerCase();
    if (type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
      try {
        const dataUrl = await pdfToImage(file);
        setPreview(dataUrl);
        toast.success("PDF page 1 ready to scan");
      } catch (e) {
        toast.error(`PDF conversion failed: ${e.message || e}`);
      }
      return;
    }
    if (!type.startsWith("image/")) {
      toast.error("Only images and PDFs are supported.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image too large. Max 8 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) await handleFile(file);
  };

  const scan = async () => {
    if (!preview) {
      toast.error("Capture, upload or drop a document first");
      return;
    }
    setProcessing(true);
    setResult(null);
    try {
      const { data } = await api.post("/ocr/scan", {
        image_base64: preview,
        project_id: projectId || null,
      }, { timeout: 90000 });
      setResult(data);
      toast.success(`Categorized as ${data.category}`);
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR scan failed");
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => {
    setPreview(null);
    setFileName("");
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  const remove = async (id) => {
    await api.delete(`/ocr/documents/${id}`);
    toast.success("Deleted");
    loadHistory();
  };

  const promoteToDoc = async () => {
    if (!result?.doc_id) return;
    setPromoting(true);
    try {
      const { data } = await api.post(
        `/ocr/documents/${result.doc_id}/promote-to-document`, {},
        { timeout: 20000 },
      );
      const docId = data.document?.document_id;
      toast.success(
        data.reused
          ? "Controlled draft already exists — opening it"
          : "Controlled document draft created — awaiting review",
        {
          action: docId
            ? { label: "Open", onClick: () => navigate(`/documents?doc=${docId}`) }
            : undefined,
        },
      );
      setResult({ ...result, promoted_document_id: docId });
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promotion failed");
    } finally {
      setPromoting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-6xl">
        <div className="vq-overline mb-2">Mobile OCR</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          Snap. Extract. File.
        </h1>
        <p className="text-slate-600 mb-8 max-w-2xl">
          Photograph co-design boards, DHF pages, change decisions, PCCP, testing pictures, manufacturing steps. AI extracts text and auto-files into the correct QMS module — with full audit trail.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Capture panel */}
          <div className="lg:col-span-3">
            <div className="vq-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg text-slate-900">Capture</h2>
                {projects.length > 0 && (
                  <Select value={projectId} onValueChange={setProjectId}>
                    <SelectTrigger data-testid="ocr-project-select" className="w-56 h-9 text-sm">
                      <SelectValue placeholder="File under project (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.project_id} value={p.project_id}>
                          {p.device_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {!preview ? (
                <div
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  data-testid="ocr-drop-zone"
                  className={`border-2 border-dashed rounded-md p-10 text-center transition-colors ${
                    isDragging ? "border-[#0055FF] bg-[#0055FF]/5" : "border-slate-200"
                  }`}
                >
                  {converting ? (
                    <>
                      <Loader2 className="w-10 h-10 text-[#0055FF] mx-auto mb-4 animate-spin" />
                      <p className="text-sm text-slate-600">Converting PDF page 1…</p>
                    </>
                  ) : (
                    <>
                      <ImageIcon className={`w-10 h-10 mx-auto mb-4 ${isDragging ? "text-[#0055FF]" : "text-slate-300"}`} />
                      <p className="text-sm text-slate-700 font-semibold mb-1" data-testid="ocr-drop-hint">
                        {isDragging ? "Drop your file to upload" : "Drag & drop an image or PDF here"}
                      </p>
                      <p className="text-xs text-slate-500 mb-6">
                        JPG, PNG, WEBP up to 8 MB or PDF up to 20 MB — or use the buttons below.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button
                          onClick={() => cameraRef.current?.click()}
                          data-testid="ocr-camera-btn"
                          className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold h-11"
                        >
                          <Camera className="w-4 h-4 mr-2" /> Take photo
                        </Button>
                        <Button
                          onClick={() => fileRef.current?.click()}
                          variant="outline"
                          data-testid="ocr-upload-btn"
                          className="border-slate-300 font-semibold h-11"
                        >
                          <Upload className="w-4 h-4 mr-2" /> Choose file
                        </Button>
                      </div>
                    </>
                  )}
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="ocr-camera-input"
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,application/pdf,.pdf"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="ocr-file-input"
                  />
                </div>
              ) : (
                <div>
                  {fileName && (
                    <div className="flex items-center gap-2 mb-3 text-xs text-slate-600" data-testid="ocr-file-name">
                      <FileIcon className="w-3.5 h-3.5" /> {fileName}
                    </div>
                  )}
                  <img src={preview} alt="Captured" className="w-full max-h-96 object-contain border border-slate-200 rounded" data-testid="ocr-preview-img" />
                  <div className="flex gap-3 mt-4">
                    <Button
                      onClick={scan}
                      disabled={processing}
                      data-testid="ocr-scan-btn"
                      className="bg-slate-900 hover:bg-[#0055FF] text-white font-semibold flex-1 h-11"
                    >
                      {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing…</> : <><ScanLine className="w-4 h-4 mr-2" /> Extract & categorize</>}
                    </Button>
                    <Button
                      onClick={reset}
                      variant="outline"
                      data-testid="ocr-reset-btn"
                      className="border-slate-300 font-semibold"
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {result && (
              <div className="vq-card p-6 mt-6 vq-fade-up">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-5 h-5 text-[#0055FF]" />
                  <h3 className="font-display font-bold text-lg text-slate-900">Analysis</h3>
                  <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 ml-auto">
                    Confidence {result.confidence}%
                  </span>
                </div>

                <div className="mb-5">
                  <div className="vq-overline mb-2">Categorized as</div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <Badge className="bg-[#0055FF]/10 text-[#0055FF] border border-[#0055FF]/30 text-sm hover:bg-[#0055FF]/10" data-testid="ocr-category-badge">
                      {result.category}
                    </Badge>
                    {result.subcategory && (
                      <span className="text-sm text-slate-700 font-medium">{result.subcategory}</span>
                    )}
                    {result.iso_ref && (
                      <span className="text-xs font-mono text-slate-500 ml-2">{result.iso_ref}</span>
                    )}
                  </div>
                </div>

                <div className="mb-5 p-4 bg-emerald-50 border border-emerald-200 rounded-md">
                  <div className="vq-overline text-emerald-800 mb-1">Auto-filed</div>
                  <p className="text-sm text-emerald-900 font-medium">{result.action}</p>
                </div>

                <div className="mb-5">
                  <div className="vq-overline mb-2">Extracted text</div>
                  <pre className="whitespace-pre-wrap font-mono text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-4 max-h-64 overflow-y-auto" data-testid="ocr-extracted-text">
                    {result.text}
                  </pre>
                </div>

                {result.key_entities && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <EntityCard label="Approval" value={result.key_entities.approval_status || "—"} />
                    <EntityCard label="Signatures" value={result.key_entities.signatures_found ? "Yes" : "No"} />
                    <EntityCard label="Dates found" value={result.key_entities.dates?.length || 0} />
                    <EntityCard label="Refs found" value={result.key_entities.reference_numbers?.length || 0} />
                  </div>
                )}

                <div className="text-xs text-slate-500 border-t border-slate-100 pt-3 mt-4 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    ✓ Audit trail recorded. Version history saved. Doc ID: <span className="font-mono">{result.doc_id}</span>
                  </div>
                  {result.promoted_document_id ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/documents?doc=${result.promoted_document_id}`)}
                      data-testid="ocr-open-controlled-doc"
                      className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 font-semibold"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                      Open controlled draft
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={promoteToDoc}
                      disabled={promoting}
                      data-testid="ocr-create-controlled-draft"
                      className="bg-[#0055FF] hover:bg-[#0044cc] text-white font-semibold"
                      title="Create a controlled document draft from this OCR scan. Enters review lifecycle at state=Draft."
                    >
                      {promoting
                        ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Creating…</>
                        : <><FilePlus className="w-3.5 h-3.5 mr-1.5" /> Create controlled draft</>}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* History */}
          <div className="lg:col-span-2">
            <div className="vq-card p-6 sticky top-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display font-bold text-lg text-slate-900">Recent scans</h2>
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              {history.length === 0 ? (
                <div className="text-center py-8 text-sm text-slate-500">No scans yet.</div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {history.map((d) => (
                    <div key={d.doc_id} className="border border-slate-200 rounded-md p-3 hover:border-slate-300 transition-colors">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-mono uppercase tracking-wider hover:bg-slate-100">
                          {d.category}
                        </Badge>
                        <div className="flex items-center gap-2">
                          {d.promoted_document_id && (
                            <button
                              onClick={() => navigate(`/documents?doc=${d.promoted_document_id}`)}
                              title="Controlled draft created — click to open"
                              data-testid={`ocr-history-promoted-${d.doc_id}`}
                              className="text-[9px] font-mono uppercase tracking-wider text-emerald-700 hover:underline flex items-center gap-1"
                            >
                              <CheckCircle2 className="w-3 h-3" /> Promoted
                            </button>
                          )}
                          <button onClick={() => remove(d.doc_id)} className="text-slate-400 hover:text-red-500 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-slate-900 truncate">{d.subcategory || "Untitled"}</div>
                      <div className="text-xs text-slate-500 line-clamp-2 mt-1">{d.summary}</div>
                      <div className="text-[10px] font-mono text-slate-400 mt-2">
                        {new Date(d.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

function EntityCard({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="vq-overline text-[9px] mb-1">{label}</div>
      <div className="text-sm font-semibold text-slate-900 truncate">{value}</div>
    </div>
  );
}
